import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import type { Project } from "@/types/project";

export function usePortfolio(activeOnly = false) {
  return useQuery<Project[]>({
    queryKey: ["portfolio", { activeOnly }],
    queryFn: () =>
      api
        .get("/portfolio/", { params: { active_only: activeOnly } })
        .then((r) => r.data),
  });
}

/** Archived projects only — for the Archived tab in Portfolio. */
export function useArchivedProjects() {
  return useQuery<Project[]>({
    queryKey: ["portfolio", { archived: true }],
    queryFn: () =>
      api.get("/portfolio/", { params: { archived: true } }).then((r) => r.data),
  });
}

export function useProject(projectId: string) {
  return useQuery<Project>({
    queryKey: ["portfolio", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export interface LifecycleStage {
  key: string;
  label: string;
  actual: string | null;
  estimate: string | null;
  manual: string | null;
  /** The date to show — manual > actual > estimate. */
  date: string | null;
  source: "manual" | "actual" | "estimate";
  set_by: string | null;
  state: "done" | "current" | "projected";
}

/** The eight lifecycle milestones (submitted -> complete): actual dates
 * from the status-event log, formula estimates for what hasn't happened. */
export function useLifecycle(projectId: string) {
  return useQuery<LifecycleStage[]>({
    queryKey: ["portfolio", "lifecycle", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}/lifecycle`).then((r) => r.data),
    enabled: !!projectId,
  });
}

/** Pin (or clear, with date=null) a lifecycle stage date by hand. The
 *  PMO owns these dates: the auto-derived ones drift from reality. */
export function useSetLifecycleDate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stageKey, date }: { stageKey: string; date: string | null }) =>
      api
        .put(`/portfolio/${projectId}/lifecycle/${stageKey}`, { date })
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(["portfolio", "lifecycle", projectId], data);
      qc.invalidateQueries({ queryKey: ["portfolio", "lifecycle", projectId] });
    },
  });
}

/** Engine-computed per-role demand for a single project. Used by the
 *  Team View math-breakdown modal to walk the calculation. */
export interface ProjectRoleDemandPhase {
  phase: string;
  weekly_hours: number;
}
export interface ProjectRoleDemand {
  role_key: string;
  role_alloc_pct: number;
  weekly_hours: number;
  phase_breakdown: ProjectRoleDemandPhase[];
}
export interface ProjectDemandResponse {
  project_id: string;
  duration_weeks: number;
  total_est_hours: number;
  roles: ProjectRoleDemand[];
}
export function useProjectDemand(projectId: string | null) {
  return useQuery<ProjectDemandResponse>({
    queryKey: ["portfolio", "demand", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}/demand`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export function useNextProjectId() {
  return useQuery<{ next_id: string }>({
    queryKey: ["portfolio", "next-id"],
    queryFn: () => api.get("/portfolio/next-id").then((r) => r.data),
  });
}

/** Compact child-project rows for the Project Detail children panel.
 *  Returns [] when the project has no children (most projects). */
export interface ProjectChildSummary {
  id: string;
  name: string;
  status: string | null;
  health: string | null;
  pct_complete: number | null;
  start_date: string | null;
  end_date: string | null;
  est_hours: number | null;
  priority: string | null;
}
export function useProjectChildren(projectId: string | null) {
  return useQuery<ProjectChildSummary[]>({
    queryKey: ["portfolio", "children", projectId],
    queryFn: () =>
      api.get(`/portfolio/${projectId}/children`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/portfolio/", data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: Record<string, unknown> & { id: string }) =>
      api.patch(`/portfolio/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
      qc.invalidateQueries({ queryKey: ["forecast"] });
    },
  });
}

export function useRebaselineProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post(`/portfolio/${id}/rebaseline`, { reason: reason ?? null }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["project", vars.id] });
      qc.invalidateQueries({ queryKey: ["project-baselines", vars.id] });
    },
  });
}

export function useProjectBaselines(projectId: string | null) {
  return useQuery({
    queryKey: ["project-baselines", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}/baselines`).then((r) => r.data as Array<{
      id: number;
      prev_end_date: string | null;
      new_end_date: string;
      reason: string | null;
      set_at: string;
      set_by: string | null;
    }>),
    enabled: !!projectId,
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/portfolio/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/portfolio/${id}/archive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}

export function useUnarchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/portfolio/${id}/unarchive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase hours — the PMO's manual hours-per-phase estimate breakdown shown
// on the lifecycle card (display-only; never feeds capacity/forecast).
// ---------------------------------------------------------------------------
export interface PhaseHoursPhase {
  key: string;
  label: string;
  from_stage: string;
  to_stage: string;
  hours: number | null;
  set_by: string | null;
  set_at: string | null;
}
export interface PhaseHours {
  phases: PhaseHoursPhase[];
  total: number;
  estimate: number | null;
  estimate_source: "est_hours" | "tshirt" | null;
  unallocated: number | null;
}

export function usePhaseHours(projectId: string) {
  return useQuery<PhaseHours>({
    queryKey: ["portfolio", "phase-hours", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}/phase-hours`).then((r) => r.data),
    enabled: !!projectId,
  });
}

/** Set (or clear with hours=null) the hours for one phase. */
export function useSetPhaseHours(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseKey, hours }: { phaseKey: string; hours: number | null }) =>
      api
        .put(`/portfolio/${projectId}/phase-hours/${phaseKey}`, { hours })
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(["portfolio", "phase-hours", projectId], data);
      qc.invalidateQueries({ queryKey: ["portfolio", "phase-hours", projectId] });
      // The phase total is the project's cost point estimate — the header
      // (detail), the Portfolio table and /review (list) all read it, so
      // they must refetch or they keep showing the old sized range.
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}
